/**
 * /health-checkup-packages-vietnam
 *
 * Four expandable check-up tiers (Standard → Premium), male / female options,
 * full test lists, "Request a Quote" CTAs feeding the shared request form.
 * No photos, no prices. Coordinator wording only.
 */
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, ClipboardList, Clock, Info, Mail, MapPin, Phone,
  ShieldCheck, Sparkles, Users, Stethoscope,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import RequestAppointmentForm from '../components/RequestAppointmentForm';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { HEALTH_CHECKUP_PACKAGES, PACKAGE_DISCLAIMER } from '../data/healthCheckupPackages';
import { COMPANY_DETAILS } from '../data/company';

const SITE = 'https://vietclinics.com';
const SLUG = '/health-checkup-packages-vietnam';

const COMPANY = {
  name: `${COMPANY_DETAILS.tradingName}, trading under ${COMPANY_DETAILS.legalName}`,
  location: 'HCMC, Vietnam',
  phones: COMPANY_DETAILS.phones,
  emails: COMPANY_DETAILS.emails,
};

const GENDER_LABEL = { male: 'Male', female: 'Female' };

const HealthCheckupPackagesPage = () => {
  const [selected, setSelected] = useState({ pkg: HEALTH_CHECKUP_PACKAGES[0].name, gender: 'male' });
  const canonical = `${SITE}${SLUG}`;

  const requestQuote = (pkgName, gender) => {
    setSelected({ pkg: pkgName, gender });
    document.getElementById('request-quote')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: 'Health Check-Up Packages in Vietnam',
        description:
          'Compare Standard, Comprehensive, Advanced, Premium and VIP health check-up packages coordinated by VietClinics with hospital partners in Vietnam. Full test lists, male and female options.',
        inLanguage: 'en',
      },
      {
        '@type': 'ItemList',
        '@id': `${canonical}#packages`,
        itemListElement: HEALTH_CHECKUP_PACKAGES.map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `${p.name} Health Check-Up Package`,
          url: `${canonical}#${p.slug}`,
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Health Check-Up Packages', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Health Check-Up Packages in Vietnam | Standard to VIP | VietClinics</title>
        <meta
          name="description"
          content="Explore Standard to VIP health check-up packages in Vietnam. Request a quote through VietClinics; providers confirm tests, suitability, availability and pricing."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Health Check-Up Packages in Vietnam | VietClinics" />
        <meta property="og:description" content="Five check-up tiers with full test lists and male / female options. Tests confirmed after doctor assessment and hospital availability." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" data-testid="checkup-packages-hero">
        <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)' }} />
        <div className="relative container mx-auto px-4 max-w-7xl py-14 lg:py-20">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3 flex items-center gap-2">
              <Stethoscope className="h-3.5 w-3.5" /> Preventive care in Vietnam
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05]">
              Health Check-Up Packages
            </h1>
            <p className="text-slate-600 mt-5 text-base md:text-lg leading-relaxed">
              Five clearly structured check-up programmes — from an essential annual review to a
              private VIP executive programme — coordinated by VietClinics with partner hospitals in Vietnam.
              Expand any package to see the full test list, choose the male or female option and
              request a personalised quote.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {HEALTH_CHECKUP_PACKAGES.map((p) => (
                <a
                  key={p.slug}
                  href={`#${p.slug}`}
                  className="inline-flex items-center gap-1.5 bg-white border border-[#DCEFEC] text-[#0B4F65] font-bold text-sm rounded-full px-4 py-2 hover:bg-[#F0FAF8] transition-colors"
                  data-testid={`checkup-jump-${p.slug}`}
                >
                  {p.name} <ArrowRight className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2">
              {[
                { icon: ShieldCheck, label: 'Partner hospitals & clinics' },
                { icon: Users, label: 'Male & female options' },
                { icon: ClipboardList, label: 'Full test lists' },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <Icon className="h-3.5 w-3.5 text-[#1FA7A1]" /> {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PACKAGES ─────────────────────────────────────────────────────── */}
      <section className="py-14 lg:py-20 bg-[#F8FAFC]" data-testid="checkup-packages-list">
        <div className="container mx-auto px-4 max-w-5xl space-y-6 lg:space-y-8">
          {HEALTH_CHECKUP_PACKAGES.map((pkg) => (
            <PackageCard key={pkg.slug} pkg={pkg} onRequestQuote={requestQuote} />
          ))}

          <div
            className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900 leading-relaxed"
            data-testid="checkup-packages-disclaimer"
          >
            <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p>
              <strong>Please note:</strong> {PACKAGE_DISCLAIMER} VietClinics is an independent healthcare
              coordination platform — we are not a hospital, clinic or laboratory and do not provide
              diagnoses.
            </p>
          </div>
        </div>
      </section>

      {/* ── REQUEST A QUOTE ──────────────────────────────────────────────── */}
      <section id="request-quote" className="py-16 lg:py-20 bg-white scroll-mt-24" data-testid="checkup-request-quote">
        <div className="container mx-auto px-4 max-w-4xl">
          <RequestAppointmentForm
            key={`${selected.pkg}-${selected.gender}`}
            defaultTreatment="Executive Health Checkup"
            defaultNotes={`Health check-up package: ${selected.pkg} (${GENDER_LABEL[selected.gender]} option). Please send a personalised quote.`}
            title="Request a Quote"
            subtitle={`Tell us about yourself and we will confirm availability and a personalised quote for the ${selected.pkg} package (${GENDER_LABEL[selected.gender]} option) with a partner hospital.`}
            testIdPrefix="checkup-quote"
          />
        </div>
      </section>

      {/* ── COMPANY FOOTER STRIP ─────────────────────────────────────────── */}
      <section className="py-10 bg-[#0B4F65] text-white" data-testid="checkup-company-footer">
        <div className="container mx-auto px-4 max-w-7xl flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <p className="font-extrabold text-lg tracking-tight">{COMPANY.name}</p>
            <p className="inline-flex items-center gap-1.5 text-sm text-white/80 mt-1">
              <MapPin className="h-3.5 w-3.5" /> {COMPANY.location}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-x-8 gap-y-3 text-sm">
            <div className="space-y-1.5">
              {COMPANY.phones.map((p) => (
                <a key={p} href={`tel:${p.replace(/\s+/g, '')}`} className="flex items-center gap-2 hover:text-[#7FE0DB] transition-colors" data-testid={`checkup-footer-phone-${p.replace(/\D/g, '')}`}>
                  <Phone className="h-3.5 w-3.5 text-[#7FE0DB]" /> {p}
                </a>
              ))}
            </div>
            <div className="space-y-1.5">
              {COMPANY.emails.map((e) => (
                <a key={e} href={`mailto:${e}`} className="flex items-center gap-2 hover:text-[#7FE0DB] transition-colors" data-testid={`checkup-footer-email-${e.split('@')[0].replace('.', '-')}`}>
                  <Mail className="h-3.5 w-3.5 text-[#7FE0DB]" /> {e}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* ── Package card ─────────────────────────────────────────────────────────── */
const PackageCard = ({ pkg, onRequestQuote }) => {
  const [gender, setGender] = useState('male');
  const isTop = pkg.tier === HEALTH_CHECKUP_PACKAGES.length;
  const extras = pkg.genderExtras?.[gender];

  return (
    <article
      id={pkg.slug}
      className={`bg-white rounded-[28px] border overflow-hidden scroll-mt-24 ${isTop ? 'border-[#1FA7A1]' : 'border-slate-100'}`}
      style={{ boxShadow: '0 20px 60px rgba(15,42,85,0.08)' }}
      data-testid={`checkup-package-${pkg.slug}`}
    >
      <div className="p-6 lg:p-9">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest">Package {pkg.tier} of {HEALTH_CHECKUP_PACKAGES.length}</span>
              {isTop && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider text-white" style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)' }}>
                  <Sparkles className="h-3 w-3" /> Most complete
                </span>
              )}
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">{pkg.name}</h2>
            <p className="text-[#1A8F8A] font-bold mt-1">{pkg.tagline}</p>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed mt-4">{pkg.summary}</p>

            <ul className="grid sm:grid-cols-3 gap-y-2 gap-x-4 mt-5">
              {pkg.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                  <span className="leading-snug">{h}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:w-64 flex-shrink-0 space-y-4">
            <div className="bg-[#F8FAFC] rounded-2xl border border-slate-100 p-4">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Best suited for</p>
              <ul className="space-y-1.5">
                {pkg.bestFor.map((b) => (
                  <li key={b} className="text-sm text-slate-700 leading-snug flex items-start gap-2">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#1FA7A1] flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <p className="inline-flex items-center gap-1.5 text-xs text-slate-500 mt-3">
                <Clock className="h-3.5 w-3.5" /> {pkg.duration}
              </p>
            </div>

            <div>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Option</p>
              <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-xl p-1" role="tablist">
                {pkg.genders.map((g) => (
                  <button
                    key={g}
                    type="button"
                    role="tab"
                    aria-selected={gender === g}
                    onClick={() => setGender(g)}
                    className={`text-sm font-bold rounded-lg py-2 transition-colors ${gender === g ? 'bg-white text-[#0B4F65] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                    data-testid={`checkup-${pkg.slug}-gender-${g}`}
                  >
                    {GENDER_LABEL[g]}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => onRequestQuote(pkg.name, gender)}
              className="w-full inline-flex items-center justify-center gap-2 text-white font-bold rounded-2xl px-6 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)', boxShadow: '0 12px 24px rgba(31,167,161,0.30)' }}
              data-testid={`checkup-${pkg.slug}-request-quote`}
            >
              Request a Quote <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Expandable test lists */}
        <div className="mt-7 border-t border-slate-100 pt-2">
          {pkg.includesPrevious && (
            <p className="text-xs font-bold text-[#1A8F8A] uppercase tracking-wider mt-3 mb-1 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> {pkg.includesPrevious}
            </p>
          )}
          <Accordion type="multiple" className="w-full" data-testid={`checkup-${pkg.slug}-accordion`}>
            {pkg.sections.map((s, i) => (
              <TestGroup key={s.title} id={`${pkg.slug}-${i}`} title={s.title} items={s.items} />
            ))}
            {extras && (
              <TestGroup id={`${pkg.slug}-${gender}`} title={`${extras.title} — additional tests`} items={extras.items} accent />
            )}
          </Accordion>
        </div>
      </div>
    </article>
  );
};

const TestGroup = ({ id, title, items, accent = false }) => (
  <AccordionItem value={id} className="border-slate-100" data-testid={`checkup-group-${id}`}>
    <AccordionTrigger className="hover:no-underline py-4 text-left">
      <span className={`font-extrabold text-sm md:text-base ${accent ? 'text-[#1A8F8A]' : 'text-slate-900'}`}>
        {title} <span className="text-slate-400 font-semibold text-xs ml-2">{items.length} item{items.length === 1 ? '' : 's'}</span>
      </span>
    </AccordionTrigger>
    <AccordionContent>
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pb-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-slate-700">
            <CheckCircle2 className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
            <span className="leading-snug">{it}</span>
          </li>
        ))}
      </ul>
    </AccordionContent>
  </AccordionItem>
);

export default HealthCheckupPackagesPage;
